import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authService, cursosService, usersService, optionsService } from '../services/api';
import RichTextEditor from '../components/RichTextEditor';
import { formatFullName } from '../utils/personName';
import { hoursToDurationInput, durationInputToHours } from '../utils/duration';
import ConfirmPopup from '../components/ConfirmPopup';
import './Cursos.css';

const ROLES_EQUIPO = ['Profesor', 'Tutor', 'Pasante', 'Ayudante'];

const emptyForm = () => ({
  nombre: '',
  descripcion: '',
  link_clase: '',
  codigo: '',
  nivel_id: '',
  duracion_clase: '',
  clases_por_semana: '',
  dias_semana: [],
  hora_inicio_clase: '',
  tomar_asistencia_durante: 'horario',
  fecha_inicio: '',
  fecha_fin: '',
  fecha_inicio_inscripcion: '',
  fecha_fin_inscripcion: '',
  cupo_maximo: 0,
  mostrar_en_home: false,
  activo: true,
  entidad: '',
  equipo: [],
});

const CursoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id && id !== 'nuevo');

  const [formData, setFormData] = useState(emptyForm());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [equipo, setEquipo] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCoordinador, setIsCoordinador] = useState(false);
  const [curso, setCurso] = useState(null);
  const [codigoParaCopiar, setCodigoParaCopiar] = useState('');
  const [loadingPorCodigo, setLoadingPorCodigo] = useState(false);
  const [errorPorCodigo, setErrorPorCodigo] = useState('');
  const [nivelesCurso, setNivelesCurso] = useState([]);

  const handleCargarPorCodigo = async () => {
    const cod = (codigoParaCopiar || '').trim();
    if (!cod) {
      setErrorPorCodigo('Escribí el código del curso.');
      return;
    }
    setErrorPorCodigo('');
    setLoadingPorCodigo(true);
    try {
      const data = await cursosService.getCursoPorCodigo(cod);
      const nivelId = data.nivel != null ? String(data.nivel) : (data.nivel_id != null ? String(data.nivel_id) : '');
      setFormData(prev => ({
        ...prev,
        nombre: data.nombre || prev.nombre,
        descripcion: data.descripcion != null ? data.descripcion : prev.descripcion,
        codigo: prev.codigo || cod,
        nivel_id: nivelId || prev.nivel_id,
      }));
    } catch (err) {
      setErrorPorCodigo(err.response?.data?.error || 'No se encontró un curso con ese código.');
    } finally {
      setLoadingPorCodigo(false);
    }
  };

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    const savedRole = localStorage.getItem('selectedRole');
    setIsCoordinador(savedRole === 'Coordinador');
  }, [navigate]);

  useEffect(() => {
    if (currentUser === null) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [users, niveles] = await Promise.all([
          usersService.getUsers(),
          optionsService.getNivelesCurso().catch(() => []),
        ]);
        setNivelesCurso(Array.isArray(niveles) ? niveles : []);
        const rolesPermitidos = ['Profesor', 'Tutor', 'Pasante', 'Ayudante'];
        const equipoData = (users || []).filter(u => u.groups && u.groups.some(g => rolesPermitidos.includes(typeof g === 'string' ? g : (g && g.name))));
        setEquipo(equipoData);
        if (isEdit) {
          const c = await cursosService.getCurso(id);
          setCurso(c);
          let equipoList = [];
          if (c.equipo && Array.isArray(c.equipo)) {
            const first = c.equipo[0];
            if (typeof first === 'object' && first !== null && ('id' in first || 'user_id' in first)) {
              equipoList = c.equipo.map(m => ({ user_id: m.user_id ?? m.id, rol: m.rol || 'Profesor' }));
            } else {
              equipoList = c.equipo.map(idd => ({ user_id: parseInt(idd, 10), rol: 'Profesor' }));
            }
          }
          setFormData({
            nombre: c.nombre || '',
            descripcion: c.descripcion || '',
            link_clase: c.link_clase || '',
            codigo: c.codigo || '',
            nivel_id: c.nivel != null ? String(c.nivel) : '',
            duracion_clase: hoursToDurationInput(c.duracion_clase),
            clases_por_semana: c.clases_por_semana || '',
            dias_semana: Array.isArray(c.dias_semana) ? [...c.dias_semana] : [],
            hora_inicio_clase: c.hora_inicio_clase ? String(c.hora_inicio_clase).substring(0, 5) : '',
            tomar_asistencia_durante: c.tomar_asistencia_durante || 'horario',
            fecha_inicio: c.fecha_inicio || '',
            fecha_fin: c.fecha_fin || '',
            fecha_inicio_inscripcion: c.fecha_inicio_inscripcion || '',
            fecha_fin_inscripcion: c.fecha_fin_inscripcion || '',
            cupo_maximo: c.cupo_maximo || 0,
            mostrar_en_home: c.mostrar_en_home === true,
            activo: c.activo !== undefined ? c.activo : true,
            entidad: c.entidad || '',
            equipo: equipoList,
          });
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Error al cargar');
        if (isEdit) setCurso(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser, id, isEdit]);

  useEffect(() => {
    if (!isEdit || !curso || !currentUser) return;
    if (isCoordinador) return;
    const estaEnEquipo = curso.equipo && Array.isArray(curso.equipo) &&
      curso.equipo.some(m => {
        const uid = typeof m === 'object' && m && (m.id != null || m.user_id != null) ? (m.user_id ?? m.id) : m;
        return parseInt(uid) === parseInt(currentUser.id);
      });
    if (!estaEnEquipo) {
      setError('Solo puedes editar los cursos en los que estás asignado.');
      return;
    }
    const getMiRolEnCurso = () => {
      const miembro = curso.equipo.find(m => {
        const uid = typeof m === 'object' && m && (m.id != null || m.user_id != null) ? (m.user_id ?? m.id) : m;
        return parseInt(uid) === parseInt(currentUser.id);
      });
      return miembro && typeof miembro === 'object' && miembro.rol ? miembro.rol : (miembro ? 'Profesor' : null);
    };
    const miRol = getMiRolEnCurso();
    if (miRol !== 'Profesor' && miRol !== 'Tutor') {
      setError('Solo el Profesor o Tutor del curso pueden editarlo.');
    }
  }, [isEdit, curso, currentUser, isCoordinador]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }));
  };

  const handleDiasSemanaChange = (dia) => {
    setFormData(prev => {
      const actual = prev.dias_semana || [];
      const tiene = actual.includes(dia);
      const nuevo = tiene ? actual.filter(d => d !== dia) : [...actual, dia].sort((a, b) => a - b);
      return { ...prev, dias_semana: nuevo };
    });
  };

  const addEquipoMember = (userId) => {
    const id = parseInt(userId, 10);
    if (!id || formData.equipo.some(m => m.user_id === id)) return;
    setFormData(prev => ({ ...prev, equipo: [...prev.equipo, { user_id: id, rol: 'Profesor' }] }));
  };

  const removeEquipoMember = (index) => {
    setFormData(prev => ({ ...prev, equipo: prev.equipo.filter((_, i) => i !== index) }));
  };

  const setEquipoRol = (index, rol) => {
    setFormData(prev => ({ ...prev, equipo: prev.equipo.map((m, i) => i === index ? { ...m, rol } : m) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const submitData = { ...formData };
      submitData.duracion_clase = durationInputToHours(submitData.duracion_clase);
      if (submitData.clases_por_semana) submitData.clases_por_semana = parseInt(submitData.clases_por_semana);
      submitData.cupo_maximo = parseInt(submitData.cupo_maximo, 10) || 0;
      submitData.dias_semana = Array.isArray(submitData.dias_semana) ? submitData.dias_semana.map(d => parseInt(d, 10)) : [];
      if (submitData.hora_inicio_clase && submitData.hora_inicio_clase.length === 5) submitData.hora_inicio_clase = `${submitData.hora_inicio_clase}:00`;
      submitData.tomar_asistencia_durante = 'horario'; // Siempre 'horario' por defecto
      submitData.codigo = (submitData.codigo || '').trim() || null;
      submitData.link_clase = (submitData.link_clase || '').trim() || null;
      submitData.entidad = (submitData.entidad || '').trim();
      submitData.nivel = (submitData.nivel_id && submitData.nivel_id !== '') ? parseInt(submitData.nivel_id, 10) : null;
      delete submitData.nivel_id;
      if (!Array.isArray(submitData.equipo)) submitData.equipo = [];
      else submitData.equipo = submitData.equipo
        .map(m => (typeof m === 'object' && m && 'user_id' in m) ? { user_id: parseInt(m.user_id, 10), rol: m.rol || 'Profesor' } : { user_id: parseInt(m, 10), rol: 'Profesor' })
        .filter(m => !isNaN(m.user_id));
      if (!submitData.nombre || !submitData.nombre.trim()) { setError('El nombre del curso es requerido'); return; }
      if (!submitData.duracion_clase || submitData.duracion_clase <= 0) { setError('La duración de cada clase debe ser mayor a 0'); return; }
      if (!submitData.clases_por_semana || submitData.clases_por_semana <= 0) { setError('La cantidad de clases por semana debe ser mayor a 0'); return; }
      if (!submitData.fecha_inicio) { setError('La fecha de inicio es requerida'); return; }
      if (!submitData.fecha_fin) { setError('La fecha de fin es requerida'); return; }
      if (!submitData.hora_inicio_clase || !submitData.hora_inicio_clase.trim()) { setError('La hora de inicio de la clase es requerida'); return; }
      if (isEdit) {
        await cursosService.updateCurso(id, submitData);
      } else {
        await cursosService.createCurso(submitData);
      }
      navigate('/dashboard/cursos');
    } catch (err) {
      const errorData = err.response?.data || {};
      let errorMessage = errorData.error || 'Error al guardar el curso';
      if (typeof errorData === 'object' && !errorData.error) {
        const first = Object.values(errorData).flat()[0];
        if (first) errorMessage = Array.isArray(first) ? first[0] : first;
      }
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="cursos-container">
        <div className="cursos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
      </div>
    );
  }

  if (isEdit && !curso) {
    return (
      <div className="cursos-container">
        <p className="cursos-error">{error || 'Curso no encontrado'}</p>
        <button type="button" className="cursos-btn cursos-btn-secondary" onClick={() => navigate('/dashboard/cursos')}>Volver</button>
      </div>
    );
  }

  if (!isEdit && !isCoordinador) {
    return (
      <div className="cursos-container">
        <p className="cursos-error">Solo el rol Coordinador puede crear nuevos cursos.</p>
        <button type="button" className="cursos-btn cursos-btn-secondary" onClick={() => navigate('/dashboard/cursos')}>Volver</button>
      </div>
    );
  }

  return (
    <div className="cursos-container">
      <div className="cursos-form-page-header">
        <button type="button" className="cursos-btn cursos-btn-secondary" onClick={() => navigate('/dashboard/cursos')}>
          ← Volver
        </button>
        <h1>{isEdit ? 'Editar Curso' : 'Nuevo Curso'}</h1>
      </div>
      <ConfirmPopup
        open={Boolean(error || errorPorCodigo)}
        title="Error"
        message={error || errorPorCodigo}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => { setError(''); setErrorPorCodigo(''); }}
        onCancel={() => { setError(''); setErrorPorCodigo(''); }}
        variant="danger"
      />
      <form onSubmit={handleSubmit} className="cursos-form cursos-form-page">
        <div className="cursos-form-group">
          <label htmlFor="codigo">Código</label>
          <input type="text" id="codigo" name="codigo" value={formData.codigo} onChange={handleInputChange} placeholder="Ej. PY1, Python 1 (varias ediciones pueden compartir el mismo)" maxLength={50} />
          <p className="cursos-form-hint">Opcional. Si varios cursos son la misma materia en distintas fechas, usá el mismo código para poder reutilizar título y descripción.</p>
        </div>
        <div className="cursos-form-group cursos-form-group-copiar">
          <label>Usar título y descripción de otro curso</label>
          <p className="cursos-form-hint">Ingresá el código de un curso existente para copiar su nombre y descripción (las fechas no se copian).</p>
          <div className="cursos-copiar-codigo-row">
            <input type="text" className="cursos-input-codigo" value={codigoParaCopiar} onChange={(e) => { setCodigoParaCopiar(e.target.value); setErrorPorCodigo(''); }} placeholder="Código del curso" />
            <button type="button" className="cursos-btn cursos-btn-secondary" onClick={handleCargarPorCodigo} disabled={loadingPorCodigo}>
              {loadingPorCodigo ? 'Cargando…' : 'Cargar'}
            </button>
          </div>
        </div>
        <div className="cursos-form-group">
          <label htmlFor="nombre">Nombre *</label>
          <input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleInputChange} required />
        </div>
        <div className="cursos-form-group">
          <label htmlFor="entidad">Entidad</label>
          <input
            type="text"
            id="entidad"
            name="entidad"
            value={formData.entidad}
            onChange={handleInputChange}
            list="curso-entidad-sugerencias"
            placeholder="Ej: Tecno3F, Adeema…"
            maxLength={120}
          />
          <datalist id="curso-entidad-sugerencias">
            <option value="Tecno3F" />
            <option value="Adeema" />
          </datalist>
          <p className="cursos-form-hint">Organización dueña del curso (propia o foránea). Se usa en la variable «Entidad» de la plantilla de certificado.</p>
        </div>
        <div className="cursos-form-group">
          <label htmlFor="nivel_id">Nivel</label>
          <select id="nivel_id" name="nivel_id" value={formData.nivel_id} onChange={handleInputChange} className="cursos-select-equipo-add">
            <option value="">Seleccionar nivel...</option>
            {nivelesCurso.map((n) => (
              <option key={n.id} value={n.id}>{n.nombre}</option>
            ))}
          </select>
          <p className="cursos-form-hint">Opcional. Inicial, Intermedio o Avanzado (se pueden gestionar desde el panel de administración de Django).</p>
        </div>
        <div className="cursos-form-group">
          <label htmlFor="descripcion">Descripción</label>
          <RichTextEditor
            value={formData.descripcion}
            onChange={(html) => setFormData(prev => ({ ...prev, descripcion: html }))}
            placeholder="Descripción del curso..."
            minHeight="140px"
          />
        </div>
        <div className="cursos-form-group">
          <label htmlFor="link_clase">Link de la clase (reunión virtual)</label>
          <input
            type="url"
            id="link_clase"
            name="link_clase"
            value={formData.link_clase}
            onChange={handleInputChange}
            placeholder="https://..."
          />
          <p className="cursos-form-hint">Opcional. Si lo cargás, en “Mis Cursos” se verá el botón de Reunión Virtual.</p>
        </div>
        <div className="cursos-form-row">
          <div className="cursos-form-group">
            <label htmlFor="duracion_clase">Duración de cada clase *</label>
            <input
              type="time"
              id="duracion_clase"
              name="duracion_clase"
              value={formData.duracion_clase}
              onChange={handleInputChange}
              required
              step="60"
            />
            <p className="cursos-form-hint">Formato hora: por ejemplo 01:30 = una hora y media; 02:00 = dos horas.</p>
          </div>
          <div className="cursos-form-group">
            <label htmlFor="clases_por_semana">Clases por semana *</label>
            <input type="number" id="clases_por_semana" name="clases_por_semana" value={formData.clases_por_semana} onChange={handleInputChange} min="1" required />
          </div>
        </div>
        {parseInt(formData.clases_por_semana, 10) > 1 && (
          <div className="cursos-form-group">
            <label>Días de la semana *</label>
            <div className="cursos-dias-semana">
              {[0, 1, 2, 3, 4, 5, 6].map(n => ({ n, label: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'][n] })).map(({ n, label }) => (
                <label key={n} className="cursos-dia-checkbox">
                  <input type="checkbox" checked={(formData.dias_semana || []).includes(n)} onChange={() => handleDiasSemanaChange(n)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="cursos-form-group">
          <label htmlFor="hora_inicio_clase">Hora de inicio de la clase *</label>
          <input type="time" id="hora_inicio_clase" name="hora_inicio_clase" value={formData.hora_inicio_clase} onChange={handleInputChange} required />
        </div>
        <div className="cursos-form-row">
          <div className="cursos-form-group">
            <label htmlFor="fecha_inicio">Fecha de Inicio *</label>
            <input type="date" id="fecha_inicio" name="fecha_inicio" value={formData.fecha_inicio} onChange={handleInputChange} />
          </div>
          <div className="cursos-form-group">
            <label htmlFor="fecha_fin">Fecha de Fin *</label>
            <input type="date" id="fecha_fin" name="fecha_fin" value={formData.fecha_fin} onChange={handleInputChange} />
          </div>
        </div>
        <div className="cursos-form-row">
          <div className="cursos-form-group">
            <label htmlFor="fecha_inicio_inscripcion">Inicio de inscripción *</label>
            <input type="date" id="fecha_inicio_inscripcion" name="fecha_inicio_inscripcion" value={formData.fecha_inicio_inscripcion} onChange={handleInputChange} />
          </div>
          <div className="cursos-form-group">
            <label htmlFor="fecha_fin_inscripcion">Fin de inscripción *</label>
            <input type="date" id="fecha_fin_inscripcion" name="fecha_fin_inscripcion" value={formData.fecha_fin_inscripcion} onChange={handleInputChange} />
          </div>
        </div>
        <p className="cursos-form-hint">La inscripción estará abierta cuando la fecha actual esté entre inicio y fin de inscripción.</p>
        <div className="cursos-form-group">
          <label htmlFor="cupo_maximo">Cupo Máximo (0 = sin límite)</label>
          <input type="number" id="cupo_maximo" name="cupo_maximo" value={formData.cupo_maximo} onChange={handleInputChange} min="0" />
        </div>
        <div className="cursos-form-group cursos-form-group-equipo">
          <label>Equipo del curso</label>
          {equipo.length === 0 ? (
            <div className="cursos-no-equipo"><p>No hay miembros del equipo disponibles.</p></div>
          ) : (
            <>
              <div className="cursos-equipo-add">
                <select className="cursos-select-equipo-add" value="" onChange={(e) => { addEquipoMember(e.target.value); e.target.value = ''; }}>
                  <option value="">Seleccionar usuario...</option>
                  {equipo.filter(m => !formData.equipo.some(e => e.user_id === m.id)).map(miembro => (
                    <option key={miembro.id} value={miembro.id}>{formatFullName(miembro.first_name, miembro.last_name)} ({miembro.email})</option>
                  ))}
                </select>
              </div>
              {formData.equipo.length > 0 && (
                <div className="cursos-equipo-lista">
                  {formData.equipo.map((m, index) => {
                    const miembro = equipo.find(u => u.id === m.user_id);
                    return miembro ? (
                      <div key={`${m.user_id}-${index}`} className="cursos-equipo-fila">
                        <span className="cursos-equipo-nombre">{formatFullName(miembro.first_name, miembro.last_name)}</span>
                        <select className="cursos-equipo-rol" value={m.rol || 'Profesor'} onChange={(e) => setEquipoRol(index, e.target.value)}>
                          {ROLES_EQUIPO.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <button type="button" className="cursos-equipo-quitar" onClick={() => removeEquipoMember(index)} title="Quitar">✕</button>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
            </>
          )}
        </div>
        <div className="cursos-form-group">
          <label className="cursos-checkbox-label">
            <input type="checkbox" name="mostrar_en_home" checked={formData.mostrar_en_home} onChange={handleInputChange} />
            <span>Mostrar en la home</span>
          </label>
        </div>
        <div className="cursos-form-group">
          <label className="cursos-checkbox-label">
            <input type="checkbox" name="activo" checked={formData.activo} onChange={handleInputChange} />
            <span>Curso activo</span>
          </label>
        </div>
        <div className="cursos-modal-actions">
          <button type="button" className="cursos-btn cursos-btn-secondary" onClick={() => navigate('/dashboard/cursos')}>Cancelar</button>
          <button type="submit" className="cursos-btn cursos-btn-primary">{isEdit ? 'Actualizar' : 'Crear'}</button>
        </div>
      </form>
    </div>
  );
};

export default CursoForm;
